import { Flex, IconButton, Text } from "@radix-ui/themes";
import {
  FaBackwardStep,
  FaForwardStep,
  FaPause,
  FaPlay,
} from "react-icons/fa6";
import { HiMusicalNote } from "react-icons/hi2";
import { LuX } from "react-icons/lu";
import { Link } from "@tanstack/react-router";

import {
  useAudioPlayerState,
  useAudioPlayerControls,
  useAudioPlayerQueue,
  useAudioPlayerVolume,
} from "@/components/providers/AudioPlayerProvider/useAudioPlayer";
import { formatMSTime } from "@/lib/utils";

import VolumeControl from "./VolumeControl";
import ProgressBar from "./ProgressBar";
import ContainerWrapper from "@/components/ui/ContainerWrapper";

export default function PlayerBar() {
  const {
    currentTrack,
    isPlaying,
    currentTimeMs,
    durationMs,
    hasPrevious,
    hasNext,
    volume,
    isMuted,
  } = useAudioPlayerState();
  const { togglePlay, playNext, playPrevious, seekTo } =
    useAudioPlayerControls();
  const { clearQueue } = useAudioPlayerQueue();
  const { setVolume, toggleMute } = useAudioPlayerVolume();

  return (
    <Flex className="fixed left-0 right-0 bottom-0 z-[60] w-screen flex-col">
      {/* Close lip — visible on md and under only */}
      {/* This is a temporary fix until we have a design for medium/small screens */}
      <div className="md:hidden self-end mr-0 flex items-center justify-center bg-[#050506] rounded-t-[5px] px-2 h-7 shadow-[-2px_-4px_8px_rgba(0,0,0,0.2)] -mb-px">
        <IconButton
          variant="ghost"
          size="2"
          radius="full"
          aria-label="Close player"
          className="text-white/70 hover:text-white hover:bg-[rgba(255,255,255,0.1)] cursor-pointer"
          onClick={clearQueue}
        >
          <LuX className="w-6 h-6" />
        </IconButton>
      </div>

      <Flex className="w-full">
        <Flex className="pointer-events-auto flex h-[72px] w-full items-center gap-6 bg-[#050506] justify-center text-white shadow-[0_-4px_12px_rgba(0,0,0,0.25)]">
          <ContainerWrapper>
            <Flex className="items-center gap-[20px] flex-1 w-full">
              <IconButton
                variant="ghost"
                size="4"
                radius="full"
                aria-label="Play previous track"
                className="text-white hover:bg-[rgba(255,255,255,0.1)] disabled:text-[rgba(255,255,255,0.5)] cursor-pointer"
                onClick={playPrevious}
                disabled={!hasPrevious}
              >
                <FaBackwardStep />
              </IconButton>

              <IconButton
                size="4"
                radius="full"
                aria-label={isPlaying ? "Pause" : "Play"}
                className="bg-white text-black text-[20px] cursor-pointer"
                onClick={togglePlay}
              >
                {isPlaying ? <FaPause /> : <FaPlay />}
              </IconButton>

              <IconButton
                variant="ghost"
                size="4"
                radius="full"
                aria-label="Play next track"
                className="text-white hover:bg-[rgba(255,255,255,0.1)] disabled:text-[rgba(255,255,255,0.5)] cursor-pointer"
                onClick={playNext}
                disabled={!hasNext}
              >
                <FaForwardStep />
              </IconButton>

              <Flex className="min-w-[220px] items-center gap-4 ml-[15px]">
                <Flex className="h-11 w-11 overflow-hidden rounded-[10px] bg-[#1B1D25] ring-1 ring-white/10">
                  {currentTrack?.artworkUrl ? (
                    <img
                      src={currentTrack.artworkUrl}
                      alt={`${currentTrack?.title} cover art`}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <Flex className="flex h-full w-full items-center justify-center">
                      <HiMusicalNote className="h-5 w-5 text-white/30" />
                    </Flex>
                  )}
                </Flex>
                <Flex direction="column">
                  <span className="font-['Figtree'] text-[16px] font-bold leading-[20px] tracking-[-0.18px] text-white w-full max-w-[150px] line-clamp-[1]">
                    {currentTrack?.title}
                  </span>
                  <Link
                    to="/creators/$CREATOR_ID"
                    params={{ CREATOR_ID: currentTrack?.creatorId || "" }}
                    className="font-['Figtree'] text-[12px] font-semibold leading-[20px] tracking-[-0.18px] text-white w-full max-w-[150px] line-clamp-[1] hover:underline"
                  >
                    {currentTrack?.creator}
                  </Link>
                </Flex>
              </Flex>

              <Flex className="flex-1 items-center gap-3">
                <Text className="text-[13px] font-semibold text-white/80 tabular-nums">
                  {formatMSTime(currentTimeMs)}
                </Text>
                <ProgressBar
                  currentTimeMs={currentTimeMs}
                  durationMs={durationMs}
                  onSeek={seekTo}
                />
                <Text className="text-[13px] font-semibold text-white/80 tabular-nums">
                  {formatMSTime(durationMs)}
                </Text>
              </Flex>

              <VolumeControl
                volume={volume}
                isMuted={isMuted}
                onVolumeChange={setVolume}
                onToggleMute={toggleMute}
              />

              <IconButton
                variant="ghost"
                size="3"
                radius="full"
                aria-label="Clear Queue"
                className="text-white hover:bg-[rgba(255,255,255,0.1)] disabled:text-[rgba(255,255,255,0.7)] cursor-pointer hidden md:block"
                onClick={clearQueue}
              >
                <LuX />
              </IconButton>
            </Flex>
          </ContainerWrapper>
        </Flex>
      </Flex>
    </Flex>
  );
}
